import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../store/authStore';

export default function AdminOrders() {
  const { token } = useAuthStore();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await fetch('http://localhost:3001/api/admin/orders', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setOrders(data);
      } else {
        setError('Failed to load orders');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Loading orders...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <nav className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <a href="/admin" className="text-blue-600 hover:text-blue-800">
            ← Back to Admin
          </a>
          <h1 className="text-2xl font-bold text-gray-900 mt-2">Order Management</h1>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {selectedOrder ? (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="mb-4">
              <button
                onClick={() => setSelectedOrder(null)}
                className="text-blue-600 hover:text-blue-800"
              >
                ← Back to Orders List
              </button>
            </div>
            <h2 className="text-xl font-bold mb-4">Order Details</h2>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <label className="text-gray-600 text-sm">Order ID</label>
                <div className="text-lg font-semibold">{selectedOrder.id}</div>
              </div>
              <div>
                <label className="text-gray-600 text-sm">Status</label>
                <div
                  className={`text-lg font-semibold ${
                    selectedOrder.status === 'completed'
                      ? 'text-green-600'
                      : selectedOrder.status === 'pending'
                      ? 'text-yellow-600'
                      : 'text-red-600'
                  }`}
                >
                  {selectedOrder.status?.toUpperCase()}
                </div>
              </div>
              <div>
                <label className="text-gray-600 text-sm">Customer Email</label>
                <div className="text-lg font-semibold">{selectedOrder.customerId}</div>
              </div>
              <div>
                <label className="text-gray-600 text-sm">Created At</label>
                <div className="text-lg">
                  {new Date(selectedOrder.createdAt).toLocaleDateString()}
                </div>
              </div>
            </div>

            <div className="border-t pt-6">
              <h3 className="text-lg font-bold mb-4">Order Items</h3>
              {selectedOrder.items ? (
                <div className="space-y-4">
                  {selectedOrder.items.map((item) => (
                    <div key={item.id} className="border rounded p-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-gray-600 text-sm">Product</label>
                          <div>{item.productId}</div>
                        </div>
                        <div>
                          <label className="text-gray-600 text-sm">Quantity</label>
                          <div>{item.quantity}</div>
                        </div>
                        <div>
                          <label className="text-gray-600 text-sm">Price</label>
                          <div>${item.price?.toFixed(2)}</div>
                        </div>
                        <div>
                          <label className="text-gray-600 text-sm">Subtotal</label>
                          <div>${(item.price * item.quantity)?.toFixed(2)}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-600">No items in this order</p>
              )}
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-6 py-4 text-left text-gray-700 font-semibold">Order ID</th>
                  <th className="px-6 py-4 text-left text-gray-700 font-semibold">
                    Customer Email
                  </th>
                  <th className="px-6 py-4 text-left text-gray-700 font-semibold">Status</th>
                  <th className="px-6 py-4 text-left text-gray-700 font-semibold">
                    Created Date
                  </th>
                  <th className="px-6 py-4 text-left text-gray-700 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order.id} className="border-t border-gray-200">
                    <td className="px-6 py-4 text-sm font-mono">{order.id.slice(0, 8)}...</td>
                    <td className="px-6 py-4">{order.customerId}</td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-3 py-1 rounded text-sm font-semibold ${
                          order.status === 'completed'
                            ? 'bg-green-100 text-green-800'
                            : order.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {order.status?.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {new Date(order.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => setSelectedOrder(order)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
